/**
 * Job Sites sign-in list — READ-ONLY, flag-gated per-site list.
 *
 * Sources GET /job-connections/site-status-board (JWT), the read-only board
 * behind the backend flag SAIK_SITE_STATUS_BOARD_ENABLED. While the flag is OFF
 * the endpoint answers {enabled:false} and this list renders NOTHING, so the
 * component is INERT in production. It also renders nothing if the request
 * errors or times out, or if the board has no rows.
 *
 * When enabled:true it shows one row per site (ordered by `position`):
 * domain/name + auth_status + action_label. Actionable labels (Authenticate /
 * Reconnect / Sign in) are shown as DISABLED placeholder buttons clearly marked
 * for the future flow — nothing here launches a browser, logs in, or takes a
 * credential. `None` / `Unavailable` are plain, muted text.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import Card from './Card';
import { jobbotApi, type SiteStatusBoardSite } from '../api/jobbot';

/** Keep a slow/dead backend from ever leaving a stray placeholder on screen. */
const REQUEST_TIMEOUT_MS = 8000;

/** Human tone per auth status. */
const AUTH_STATUS_LABEL: Record<string, string> = {
  connected: 'Connected',
  needs_login: 'Needs sign-in',
  expired: 'Expired',
  not_configured: 'Not configured',
  unknown: 'Unknown',
};

/** Labels that mean the user has something to do (mirror the backend action set). */
const ACTIONABLE_LABELS = new Set(['Authenticate', 'Reconnect', 'Sign in']);

export default function SiteAuthList() {
  const [sites, setSites] = useState<SiteStatusBoardSite[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      try {
        const res = await jobbotApi.extension.siteStatusBoard({ signal: controller.signal });
        const data = res.data;
        if (!alive) return;
        // Flag OFF → endpoint returns enabled:false → render nothing (inert).
        setSites(data?.enabled ? (data.sites || []) : null);
      } catch {
        // Network error / timeout / disabled → render nothing.
        if (alive) setSites(null);
      } finally {
        window.clearTimeout(timer);
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  if (loading) return null; // never flash a placeholder before we know the flag
  if (!sites || sites.length === 0) return null; // flag OFF / error / empty → inert

  const ordered = [...sites].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <Card className="mb-6 p-4" data-agent-id="jobbot.sites.auth-list">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck size={18} className="text-brand-600" aria-hidden="true" />
        <h2 className="font-semibold text-slate-950 dark:text-slate-100">Job Sites sign-in status</h2>
      </div>
      <p className="mb-3 text-xs text-slate-600 dark:text-slate-500">
        Read-only. Sign-in actions are coming soon.
      </p>
      <ul className="space-y-2">
        {ordered.map((site) => {
          const name = site.name || site.domain || site.site_id;
          const action = site.action_label || 'None';
          const actionable = ACTIONABLE_LABELS.has(action);
          const statusLabel = AUTH_STATUS_LABEL[site.auth_status || 'unknown'] || site.auth_status;
          return (
            <li
              key={site.site_id}
              data-agent-id={`jobbot.sites.auth-list.row.${site.site_id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-950 dark:text-slate-100" title={name}>{name}</p>
                {site.domain && site.domain !== name && (
                  <p className="truncate text-xs text-slate-600 dark:text-slate-500" title={site.domain}>{site.domain}</p>
                )}
                <span
                  className="mt-1 inline-block rounded-md bg-slate-100 dark:bg-slate-700/50 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300"
                  data-agent-id={`jobbot.sites.auth-list.status.${site.site_id}`}
                >
                  {statusLabel}
                </span>
              </div>
              <div className="shrink-0">
                {actionable ? (
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    title="Coming soon"
                    data-agent-id={`jobbot.sites.auth-list.action.${site.site_id}`}
                    className="cursor-not-allowed rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400"
                  >
                    {action} (coming soon)
                  </button>
                ) : (
                  <span
                    className="text-xs text-slate-500 dark:text-slate-500"
                    data-agent-id={`jobbot.sites.auth-list.action.${site.site_id}`}
                  >
                    {action}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
